import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  IconButton,
  Text,
  Stack,
  Separator,
  DefaultButton,
  PrimaryButton,
  Dialog,
  DialogType,
  DialogFooter,
} from "@fluentui/react";
import { File, getFileByInternalId, deleteFile, downloadFile, getFileSecureLink } from "@/api/files";
import { useErrorNotification } from "@/utils/useErrorNotification";
import TagsPanel from "@/components/files/TagsPanel";
import SecureLinkModal from "@/components/files/SecureLinkModal";

const FileDetailsPage = () => {
  const { internalFileId } = useParams<{ internalFileId: string }>();
  const navigate = useNavigate();
  const { errorMessage, showError, clearError } = useErrorNotification();
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isTagsPanelOpen, setIsTagsPanelOpen] = useState<boolean>(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [isSecureLinkModalOpen, setIsSecureLinkModalOpen] = useState<boolean>(false);
  const [secureLinkData, setSecureLinkData] = useState<{
    file?: File;
    link?: string;
    loading: boolean;
    error?: string;
  }>({ loading: false });

  useEffect(() => {
    if (internalFileId) {
      loadFile();
    }
  }, [internalFileId]);

  const loadFile = async () => {
    if (!internalFileId) {
      return;
    }

    setLoading(true);
    try {
      const fileData = await getFileByInternalId(internalFileId);
      setFile(fileData);
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/files");
  };

  const handleDownload = async () => {
    if (!file) {
      return;
    }

    try {
      const response = await downloadFile(file.id);
      if (response && response.data) {
        // Create a blob from the response data
        const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
        
        // Create a temporary link element to trigger download
        const link = document.createElement('a');
        const blobUrl = URL.createObjectURL(blob);
        link.href = blobUrl;
        link.download = file.fileName || `file_${file.id}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up the blob URL
        URL.revokeObjectURL(blobUrl);
      } else {
        console.warn("No data received for file:", file.id);
      }
    } catch (error) {
      showError(error);
    }
  };

  const handleView = async () => {
    if (!file) {
      return;
    }

    try {
      const response = await downloadFile(file.id);
      if (response && response.data) {
        // Create a blob URL for viewing
        const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, "_blank");
        
        // Clean up the blob URL after a delay to allow the browser to load it
        setTimeout(() => {
          URL.revokeObjectURL(blobUrl);
        }, 1000);
      } else {
        console.warn("No data received for file:", file.id);
      }
    } catch (error) {
      showError(error);
    }
  };

  const handleSecureLink = async () => {
    if (!file) {
      return;
    }

    setSecureLinkData({ file, loading: true });
    setIsSecureLinkModalOpen(true);
    
    try {
      const secureUrl = await getFileSecureLink(file.id);
      if (secureUrl) {
        setSecureLinkData({ file, link: secureUrl, loading: false });
      } else {
        setSecureLinkData({ 
          file, 
          loading: false, 
          error: "No secure URL returned for this file." 
        });
      }
    } catch (error) {
      console.error("Error getting secure link for file:", file.id, error);
      setSecureLinkData({ 
        file, 
        loading: false, 
        error: "Failed to generate secure link. Please try again." 
      });
    }
  };

  const handleDelete = async () => {
    if (!file) {
      return;
    }

    try {
      await deleteFile(file.id);
      setIsDeleteDialogOpen(false);
      navigate("/files");
    } catch (error) {
      showError(error);
      setIsDeleteDialogOpen(false);
    }
  };

  const handleCloseSecureLinkModal = () => {
    setIsSecureLinkModalOpen(false);
    setSecureLinkData({ loading: false });
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  const formatFileSize = (sizeInKb?: number) => {
    if (!sizeInKb) return "N/A";
    if (sizeInKb < 1024) return `${sizeInKb} KB`;
    const sizeInMb = (sizeInKb / 1024).toFixed(2);
    return `${sizeInMb} MB`;
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
        }}
      >
        <Spinner size={SpinnerSize.large} label="Loading file details..." />
      </div>
    );
  }

  if (!file) {
    return (
      <div>
        <div className="page-header">
          <div className="breadcrumb-left-section">
            <div>
              <h2 className="page-breadcrumb-title">File Not Found</h2>
              <div style={{ color: "#605e5c", fontSize: "14px" }}>
                The requested file could not be found
              </div>
            </div>
          </div>
          <div>
            <DefaultButton text="Back to Files" onClick={handleBack} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-header">
        <div className="breadcrumb-left-section">
          <div>
            <h2 className="page-breadcrumb-title">File Details</h2>
            <div style={{ color: "#605e5c", fontSize: "14px" }}>
              {file.fileName || "Unnamed File"}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <DefaultButton text="Back to Files" onClick={handleBack} />
        </div>
      </div>

      <div style={{ padding: "20px", maxWidth: "1200px" }}>
        <Stack tokens={{ childrenGap: 20 }}>
          {/* Basic and Technical Information Side by Side */}
          <div style={{ display: "flex", gap: "40px", flexWrap: "wrap" }}>
            {/* Basic Information - Left Side */}
            <div style={{ flex: "1", minWidth: "400px" }}>
              <Text variant="xLarge" style={{ fontWeight: "600", marginBottom: "10px", display: "block" }}>
                Basic Information
              </Text>
              <Separator />
              <div style={{ marginTop: "15px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: "15px", alignItems: "center" }}>
                  <Text variant="medium" style={{ fontWeight: "600" }}>ID:</Text>
                  <Text variant="medium">{file.id}</Text>

                  <Text variant="medium" style={{ fontWeight: "600" }}>Internal File ID:</Text>
                  <Text variant="medium">{file.internalFileId || "N/A"}</Text>

                  <Text variant="medium" style={{ fontWeight: "600" }}>External File ID:</Text>
                  <Text variant="medium">{file.externalFileId || "N/A"}</Text>

                  <Text variant="medium" style={{ fontWeight: "600" }}>File Name:</Text>
                  <Text variant="medium">{file.fileName || "N/A"}</Text>

                  <Text variant="medium" style={{ fontWeight: "600" }}>Customer ID:</Text>
                  <Text variant="medium">{file.customerId}</Text>

                  <Text variant="medium" style={{ fontWeight: "600" }}>File Size:</Text>
                  <Text variant="medium">{formatFileSize(file.fileSizeInKb)}</Text>

                  <Text variant="medium" style={{ fontWeight: "600" }}>Status:</Text>
                  <Text 
                    variant="medium" 
                    style={{ 
                      color: file.isActive ? "#107c10" : "#d13438", 
                      fontWeight: "600" 
                    }}
                  >
                    {file.isActive ? "Active" : "Inactive"}
                  </Text>

                  <Text variant="medium" style={{ fontWeight: "600" }}>Upload Date:</Text>
                  <Text variant="medium">{formatDate(file.dateUploadedUtc)}</Text>

                  <Text variant="medium" style={{ fontWeight: "600" }}>Created Date:</Text>
                  <Text variant="medium">{formatDate(file.createdAtUtc)}</Text>

                  <Text variant="medium" style={{ fontWeight: "600" }}>Updated Date:</Text>
                  <Text variant="medium">{formatDate(file.updatedAtUtc)}</Text>
                </div>
              </div>
            </div>

            {/* Technical Information - Right Side */}
            <div style={{ flex: "1", minWidth: "400px" }}>
              <Text variant="xLarge" style={{ fontWeight: "600", marginBottom: "10px", display: "block" }}>
                Technical Information
              </Text>
              <Separator />
              <div style={{ marginTop: "15px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: "15px", alignItems: "center" }}>
                  <Text variant="medium" style={{ fontWeight: "600" }}>MIME Type ID:</Text>
                  <Text variant="medium">{file.mimeTypeId || "N/A"}</Text>

                  <Text variant="medium" style={{ fontWeight: "600" }}>Checksum:</Text>
                  <Text variant="medium" style={{ wordBreak: "break-all" }}>{file.checksum || "N/A"}</Text>

                  <Text variant="medium" style={{ fontWeight: "600" }}>Access Tier ID:</Text>
                  <Text variant="medium">{file.accessTierId || "N/A"}</Text>

                  <Text variant="medium" style={{ fontWeight: "600" }}>URL:</Text>
                  <Text variant="medium" style={{ wordBreak: "break-all" }}>{file.url || "N/A"}</Text>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          {file.isActive && (
            <div>
              <Text variant="xLarge" style={{ fontWeight: "600", marginBottom: "10px", display: "block" }}>
                Actions
              </Text>
              <Separator />
              <div style={{ marginTop: "15px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
                <PrimaryButton 
                  text="Download" 
                  iconProps={{ iconName: "Download" }}
                  onClick={handleDownload}
                />
                <DefaultButton 
                  text="View File" 
                  iconProps={{ iconName: "View" }}
                  onClick={handleView}
                />
                <DefaultButton 
                  text="Secure Link" 
                  iconProps={{ iconName: "Lock" }}
                  onClick={handleSecureLink}
                />
                <DefaultButton 
                  text="Manage Tags" 
                  iconProps={{ iconName: "Tag" }}
                  onClick={() => setIsTagsPanelOpen(true)}
                />
                <DefaultButton 
                  text="Delete" 
                  iconProps={{ iconName: "Delete" }}
                  onClick={() => setIsDeleteDialogOpen(true)}
                  styles={{
                    root: { color: "#d13438" },
                    rootHovered: { backgroundColor: "#fdf3f4", color: "#d13438" }
                  }}
                />
              </div>
            </div>
          )}
        </Stack>
      </div>

      {/* Tags Panel */}
      <TagsPanel
        fileId={file?.id}
        isOpen={isTagsPanelOpen}
        onDismiss={() => setIsTagsPanelOpen(false)}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        hidden={!isDeleteDialogOpen}
        onDismiss={() => setIsDeleteDialogOpen(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Delete File',
          subText: `Are you sure you want to delete the file "${file.fileName}"?`,
        }}
        modalProps={{
          isBlocking: false,
        }}
      >
        <DialogFooter>
          <PrimaryButton onClick={handleDelete} text="Delete" />
          <DefaultButton
            onClick={() => setIsDeleteDialogOpen(false)}
            text="Cancel"
          />
        </DialogFooter>
      </Dialog>

      {/* Secure Link Modal */}
      <SecureLinkModal
        isOpen={isSecureLinkModalOpen}
        fileName={secureLinkData.file?.fileName}
        secureLink={secureLinkData.link}
        loading={secureLinkData.loading}
        error={secureLinkData.error}
        onDismiss={handleCloseSecureLinkModal}
      />

      {/* Error Message */}
      {errorMessage && (
        <MessageBar
          messageBarType={MessageBarType.error}
          isMultiline={false}
          onDismiss={clearError}
          styles={{
            root: {
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              zIndex: 1000,
            },
          }}
        >
          {errorMessage}
        </MessageBar>
      )}
    </>
  );
};

export default FileDetailsPage;
